/**
 * Placement number formatting and comparison shared by the platen canvas and
 * its callers (Manual orders' placement editor, the Upload Studio). Pure, no
 * React. Moved out of manual-orders/lib/build-payload.ts in Phase 91, which
 * still re-exports every name.
 */
import type { ManualPrintPlacement } from '@threadheads/shared';

/** A CSS percentage: positions are percentages of the platen box. */
export const pct = (value: number) => `${value}%`;

/** Inches to the hundredth (what the payload carries). */
function roundHundredth(value: number): number {
  return Math.round(value * 100) / 100;
}

/** A placement as the payload carries it: inches rounded to 0.01. */
export function roundPlacement(placement: ManualPrintPlacement): ManualPrintPlacement {
  return {
    x_in: roundHundredth(placement.x_in),
    y_in: roundHundredth(placement.y_in),
    width_in: roundHundredth(placement.width_in),
  };
}

/** "3.5", "12.0": inches to one decimal, as the readout and the DPI pill show them. */
export function formatInches(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return (Object.is(rounded, -0) ? 0 : rounded).toFixed(1);
}

/**
 * How far the design's centre sits from the platen's centre line, inches:
 * positive = right of centre, negative = left. Under 0.05 in reads as 0.
 */
export function centreOffsetIn(placement: ManualPrintPlacement, platenWidthIn: number): number {
  const offset = placement.x_in + placement.width_in / 2 - platenWidthIn / 2;
  return Math.abs(offset) < 0.05 ? 0 : offset;
}

/** "+1.8", "−1.8" (U+2212), "0.0": a signed offset with a real minus sign. */
export function formatSignedInches(value: number): string {
  const text = formatInches(Math.abs(value));
  if (text === '0.0') return text;
  return value > 0 ? `+${text}` : `−${text}`;
}

/** True when two placements are the same to the hundredth of an inch. */
export function samePlacement(a: ManualPrintPlacement | null, b: ManualPrintPlacement | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.x_in - b.x_in) < 0.005 && Math.abs(a.y_in - b.y_in) < 0.005 && Math.abs(a.width_in - b.width_in) < 0.005
  );
}
